import { type ReactElement, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBootstrapController } from '@/app/AppBootstrapController';
import { AppDialogHost } from '@/app/AppDialogHost';
import { AppShellRouter } from '@/app/AppShellRouter';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { useUpdateUserContext } from '@/features/users';
import {
  FeatureFlagsProvider,
  useAppDialogs,
  useAppFeatureFlags,
  useNotificationsStream,
  useOrganisationEntitlements,
} from '@/shared/hooks';
import { getPlanDisplayName } from '@flowqa/shared';
import { getStoredContext } from './auth/sessionStorage';
import { useAuth } from './auth/useAuth';
import { getNavItemsFromRoutes } from './navigation/routes/nav';
import { appRouteDefinitions } from './navigation/routes/routes';
import { useNotifications } from './providers/notificationHooks';
import { useBootstrapData } from './shared/hooks/useBootstrapData';
import { useNavItems } from './shared/hooks/useNavItems';

export const App = (): ReactElement => {
  const {
    session,
    loading: authLoading,
    error: authError,
    signIn,
    signUp,
    signOut,
    refreshSession,
    setSessionDirect,
  } = useAuth();
  const bootstrap = useBootstrapData({ session });
  const { notify } = useNotifications();
  useNotificationsStream();

  const organisations = bootstrap.organisations;
  const visibleProjects = bootstrap.visibleProjects;
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const { featureFlags, featureFlagsLoading } = useAppFeatureFlags({
    routes: appRouteDefinitions,
    token: session?.tokens.accessToken ?? null,
  });
  const entitlements = entitlementsQuery.data ?? null;
  const entitlementsLoading = entitlementsQuery.isLoading;

  const projectLimit = entitlements?.plan.limits.projectLimit ?? null;
  const canCreateProject = projectLimit ? visibleProjects.length < projectLimit : true;
  const lockProjectSelector = false;

  const effectiveFeatureFlags = useMemo<Record<string, boolean> | null>(() => {
    if (!featureFlags) return null;
    const planFeatures = entitlements?.plan.features ?? null;
    const addOns = entitlements?.addOns ?? null;
    const deliveryEnabled = addOns?.deliveryEnabled === true;
    const operationsEnabled = addOns?.operationsEnabled === true;
    return {
      ...featureFlags,
      deltas: featureFlags.deltas && (planFeatures?.deltasEnabled ?? true),
      dateFilters: true,
      apiUploads: planFeatures?.apiUploadsEnabled ?? true,
      delivery: featureFlags.delivery && deliveryEnabled,
      operations: featureFlags.operations && operationsEnabled,
      organisationSelector: false,
      projectSelector: true,
      organisations: featureFlags.organisations,
    };
  }, [entitlements, featureFlags]);
  const effectiveFlagsLoading = featureFlagsLoading || entitlementsLoading;

  const navRouteDefinitions = useMemo(() => {
    if (effectiveFlagsLoading) {
      return appRouteDefinitions.filter((route) => !route.featureFlag);
    }
    const flags = effectiveFeatureFlags ?? featureFlags;
    if (!flags) return appRouteDefinitions.filter((route) => !route.featureFlag);
    return appRouteDefinitions.filter((route) => {
      if (!route.featureFlag) return true;
      return flags[route.featureFlag];
    });
  }, [effectiveFeatureFlags, effectiveFlagsLoading, featureFlags]);
  const navItems = useMemo(() => getNavItemsFromRoutes(navRouteDefinitions), [navRouteDefinitions]);
  const { navItemsForSession, isAdminUser, isSuperuser } = useNavItems(session, navItems);
  const isAdmin = isAdminUser(session);
  const isSuperuserUser = isSuperuser(session);

  const updateUserContext = useUpdateUserContext();
  const navigate = useNavigate();
  const dialogs = useAppDialogs({
    session,
    bootstrap,
    navigate: (path: string) => {
      void navigate(path);
    },
    refreshSession,
    setSessionDirect,
    updateUserContext: updateUserContext.mutateAsync,
  });

  const storedProjectId = getStoredContext().lastProjectId;
  const sessionLastOrganisationId = session?.lastOrganisationId ?? null;
  const sessionLastProjectId = session?.lastProjectId ?? null;

  return (
    <FeatureFlagsProvider
      flags={effectiveFeatureFlags ?? featureFlags}
      loading={effectiveFlagsLoading}
    >
      <AppBootstrapController
        session={session}
        authLoading={authLoading}
        authError={authError}
        signIn={signIn}
        signUp={signUp}
        signOut={signOut}
        refreshSession={refreshSession}
        setSessionDirect={setSessionDirect}
        isAdmin={isAdmin}
        isSuperuser={isSuperuserUser}
        organisations={organisations}
        bootstrap={{
          projectsLoaded: bootstrap.projectsLoaded,
          projects: bootstrap.projects,
          visibleProjectOptions: bootstrap.visibleProjectOptions,
        }}
        selectedOrganisationId={selectedOrganisationId ?? null}
        selectedProjectId={selectedProjectId ?? null}
        setSelectedOrganisationId={setSelectedOrganisationId}
        setSelectedProjectId={setSelectedProjectId}
        dialogs={dialogs}
        routeDefinitions={appRouteDefinitions}
        featureFlags={featureFlags}
        effectiveFeatureFlags={effectiveFeatureFlags}
        effectiveFlagsLoading={effectiveFlagsLoading}
        featureFlagsLoading={featureFlagsLoading}
        canCreateProject={canCreateProject}
        storedProjectId={storedProjectId}
        sessionLastOrganisationId={sessionLastOrganisationId}
        sessionLastProjectId={sessionLastProjectId}
        onRouteAddProject={() => {
          if (!canCreateProject) {
            notify({ message: 'Project limit reached', tone: 'orange' });
            return;
          }
          dialogs.openAddProject(true);
        }}
        updateUserContext={updateUserContext.mutate}
      >
        {(state) => (
          <>
            <AppShellRouter
              state={state}
              session={session}
              authError={authError}
              isAdmin={isAdmin}
              isSuperuser={isSuperuserUser}
              navItems={navItemsForSession}
              projects={bootstrap.visibleProjectOptions}
              allProjects={bootstrap.projects.map((project) => ({
                id: project.id,
                name: project.name,
                slug: project.slug,
              }))}
              organisations={organisations}
              canCreateProject={canCreateProject}
              planName={entitlements ? getPlanDisplayName(entitlements.plan.code) : null}
              lockProjectSelector={lockProjectSelector}
              onAddProject={() => {
                if (!canCreateProject) {
                  notify({ message: 'Project limit reached', tone: 'orange' });
                  return;
                }
                dialogs.openAddProject(true);
              }}
              onAddOrganisation={() => {
                dialogs.openAddOrganisation();
              }}
              onSignOut={signOut}
            />
            <AppDialogHost dialogs={dialogs} />
          </>
        )}
      </AppBootstrapController>
    </FeatureFlagsProvider>
  );
};
